import { ref } from "vue";

/**
 * @composable usePaginatedList
 * @description Gestisce il caricamento pagino di una lista con gestione degli errori.
 * @param {Function} fetcher - Funzione del service che carica i dati. Deve ritornare { data, error }
 * @param {Object} options - Opzioni di configurazione
 * @param {number} [options.pageSize=10] - Numero di elementi per pagina
 * @param {number} [options.errorResetMs=5000] - Millisecondi prima di resettare hasError (0 = l'errore resta fino al prossimo load)
 * @param {Function} [options.mapResponse] - Mapper personalizzato per la risposta (default: data => ({ items: data?.items ?? [], totalPages: data?.total_pages || 1 }))
 * @returns {{
 *   items: import('vue').Ref<Array>,
 *   currentPage: import('vue').Ref<number>,
 *   totalPages: import('vue').Ref<number>,
 *   loaded: import('vue').Ref<boolean>,
 *   hasError: import('vue').Ref<boolean>,
 *   load: Function,
 *   changePage: Function
 * }}
 */
export function usePaginatedList(
  fetcher,
  {
    pageSize = 10,
    errorResetMs = 5000,
    mapResponse = (data) => ({
      items: data?.items ?? [],
      totalPages: data?.total_pages || 1,
    }),
  } = {}
) {
  const items = ref([]);
  const currentPage = ref(1);
  const totalPages = ref(1);
  const loaded = ref(true); // true = dati disponibili (opposto di viewLoader)
  const hasError = ref(false);

  let errorResetTimeout = null;

  /**
   * Carica i dati con i parametri forniti
   * @param {Object} params - Parametri per la chiamata API
   */
  async function load(params = {}) {
    loaded.value = false;
    hasError.value = false;

    if (errorResetTimeout) {
      clearTimeout(errorResetTimeout);
    }

    const result = await fetcher({
      ...params,
      page_number: currentPage.value,
      page_size: pageSize,
    });

    if (result.error) {
      console.error(result.error);
      items.value = [];
      totalPages.value = 1;
      hasError.value = true;

      if (errorResetMs > 0) {
        errorResetTimeout = setTimeout(() => {
          hasError.value = false;
        }, errorResetMs);
      }
    } else {
      const mapped = mapResponse(result.data);
      items.value = mapped.items ?? [];
      totalPages.value = mapped.totalPages ?? 1;
    }

    loaded.value = true;
  }

  /**
   * Cambia la pagina corrente
   * @param {number} page - Numero della pagina
   */
  function changePage(page) {
    currentPage.value = page;
  }

  return {
    items,
    currentPage,
    totalPages,
    loaded,
    hasError,
    load,
    changePage,
  };
}
